/**
 * Shop by Collection Component
 * Minimal JavaScript - CSS handles button styles, JS only handles content visibility
 * Uses native radio button behavior for accessibility
 */

if (!customElements.get('shop-by-collection-component')) {
  class ShopByCollectionComponent extends HTMLElement {
    constructor() {
      super();

      // Get all filter radio inputs and collection containers
      this.filterInputs = this.closest('.shop-by-collection').querySelectorAll('.shop-by-collection__filter-input');
      this.collectionContainers = this.querySelectorAll('.shop-by-collection__collection-container');

      this.setupEventListeners();
      this.checkScrollable();
    }

    setupEventListeners() {
      // Get the filters container
      this.filtersContainer = this.closest('.shop-by-collection').querySelector('.shop-by-collection__filters');

      // Track scroll position
      this.savedScrollPosition = 0;

      // Listen to native radio input change events
      this.filterInputs.forEach(input => {
        input.addEventListener('change', this.onFilterChange.bind(this));

        // Prevent focus behavior from scrolling the container
        input.addEventListener('focus', (e) => {
          e.preventDefault();
          e.stopPropagation();
        });
      });

      // Store scroll position whenever user scrolls manually
      if (this.filtersContainer) {
        this.filtersContainer.addEventListener('scroll', () => {
          if (!this.isRestoringScroll) {
            this.savedScrollPosition = this.filtersContainer.scrollLeft;
          }
        });
      }

      // Capture scroll position before label clicks
      const labels = this.closest('.shop-by-collection').querySelectorAll('.shop-by-collection__filter-label');
      labels.forEach(label => {
        label.addEventListener('mousedown', (e) => {
          // Save current scroll position
          this.savedScrollPosition = this.filtersContainer.scrollLeft;

          // Prevent the label from triggering focus on the input
          e.preventDefault();

          // Manually trigger the click on the associated input
          const inputId = label.getAttribute('for');
          const input = document.getElementById(inputId);
          if (input) {
            input.checked = true;
            // Trigger change event manually
            input.dispatchEvent(new Event('change', { bubbles: true }));
          }
        });

        // Prevent default click behavior since we handle it in mousedown
        label.addEventListener('click', (e) => {
          e.preventDefault();
        });
      });

      // Setup filter buttons scroll behavior
      this.setupFilterScroll();
    }

    setupFilterScroll() {
      const filtersContainer = this.closest('.shop-by-collection').querySelector('.shop-by-collection__filters');
      if (!filtersContainer) return;

      // Scroll the clicked button into view
      this.filterInputs.forEach(input => {
        const label = input.nextElementSibling;
        if (!label) return;

        label.addEventListener('click', () => {
          // Small delay to ensure radio is checked before scrolling
          setTimeout(() => {
            this.scrollButtonIntoView(label, filtersContainer);
          }, 10);
        });
      });
    }

    scrollButtonIntoView(button, container) {
      // Only scroll if the container is scrollable
      if (container.scrollWidth <= container.clientWidth) return;

      const buttonRect = button.getBoundingClientRect();
      const containerRect = container.getBoundingClientRect();

      // Calculate the button's position relative to the container
      const buttonLeft = buttonRect.left - containerRect.left + container.scrollLeft;
      const buttonWidth = buttonRect.width;

      // Center the button in the container
      const scrollPosition = buttonLeft - (containerRect.width / 2) + (buttonWidth / 2);

      container.scrollTo({
        left: scrollPosition,
        behavior: 'smooth'
      });
    }

    onFilterChange(event) {
      const selectedInput = event.currentTarget;
      const collectionHandle = selectedInput.dataset.collectionHandle;

      // Show the selected collection container and hide others
      this.switchCollection(collectionHandle);

      // Restore scroll position with multiple attempts to override browser behavior
      this.restoreScrollPosition();
    }

    restoreScrollPosition() {
      if (!this.filtersContainer) return;

      this.isRestoringScroll = true;

      // Immediate restoration
      this.filtersContainer.scrollLeft = this.savedScrollPosition;

      // Restore in next frame
      requestAnimationFrame(() => {
        this.filtersContainer.scrollLeft = this.savedScrollPosition;
      });

      // Restore after a short delay to override any browser scrolling
      setTimeout(() => {
        this.filtersContainer.scrollLeft = this.savedScrollPosition;
        this.isRestoringScroll = false;
      }, 100);

      // Final restoration to ensure it sticks
      setTimeout(() => {
        this.filtersContainer.scrollLeft = this.savedScrollPosition;
      }, 200);
    }

    switchCollection(collectionHandle) {
      // Hide all collection containers
      this.collectionContainers.forEach(container => {
        if (container.dataset.collectionHandle === collectionHandle) {
          // Show the selected collection
          container.classList.remove('hidden');

          // Reset the slider to the first slide
          this.resetSlider(container);

          // Trigger scroll animations if enabled
          this.triggerScrollAnimations(container);
        } else {
          // Hide other collections
          container.classList.add('hidden');
        }
      });
    }

    resetSlider(container) {
      const sliderComponent = container.querySelector('slider-component');
      if (!sliderComponent) return;

      const slider = sliderComponent.querySelector('.slider');
      if (!slider) return;

      // Scroll to the beginning
      slider.scrollTo({ left: 0, behavior: 'instant' });

      // The SliderComponent from global.js will handle button states and counter updates
      // via its update() method which is bound to the scroll event

      // Force the slider component to recalculate pages after switching collections
      // This ensures the counter updates correctly for the new collection's product count
      if (sliderComponent.initPages && typeof sliderComponent.initPages === 'function') {
        // Wait for DOM to be fully laid out before recalculating
        // Use requestAnimationFrame to ensure layout is complete
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            sliderComponent.initPages();
          });
        });
      }
    }


    triggerScrollAnimations(container) {
      // If scroll animations are enabled, trigger them for newly visible items
      if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        const animatedItems = container.querySelectorAll('.scroll-trigger.animate--slide-in');
        animatedItems.forEach(item => {
          // Remove and re-add the animation class to retrigger
          item.classList.remove('animate--slide-in');
          // Force reflow
          void item.offsetWidth;
          item.classList.add('animate--slide-in');
        });
      }
    }

    /**
     * Check if the filters container is scrollable and add/remove class accordingly
     * This allows CSS to center buttons when they fit, or align left when scrollable
     */
    checkScrollable() {
      const filtersContainer = this.closest('.shop-by-collection').querySelector('.shop-by-collection__filters');
      if (!filtersContainer) return;

      const updateScrollableState = () => {
        // Check if content width exceeds container width (is scrollable)
        const isScrollable = filtersContainer.scrollWidth > filtersContainer.clientWidth;

        if (isScrollable) {
          filtersContainer.classList.add('is-scrollable');
        } else {
          filtersContainer.classList.remove('is-scrollable');
        }
      };

      // Check on load
      updateScrollableState();

      // Check on window resize
      window.addEventListener('resize', updateScrollableState);

      // Use ResizeObserver if available for more accurate detection
      if ('ResizeObserver' in window) {
        const resizeObserver = new ResizeObserver(updateScrollableState);
        resizeObserver.observe(filtersContainer);
      }
    }
  }

  customElements.define('shop-by-collection-component', ShopByCollectionComponent);
}
